import type { Database } from "../../__definitions__/database-definitions";

export type Assistant = Database["public"]["Tables"]["mcp_assistant_setup"]["Row"]["assistant"];

export type SetupField = { label: string; value: string } | { label: string; connectionUrl: true };

interface SetupStep {
  text: string;
  admin?: boolean;
  fields?: SetupField[];
}

interface SetupPath {
  id: string;
  label: string;
  steps: SetupStep[];
}

export interface AssistantInfo {
  id: Assistant;
  name: string;
  plan: string;
  logo: string;
  removePath: string;
  paths: SetupPath[];
}

const description = "Create, schedule and analyze employee comms";

const claudeDetails: SetupField[] = [
  { label: "Name", value: "ChangeEngine" },
  { label: "URL", connectionUrl: true },
];

const claudeSignIn: SetupField[] = [
  { label: "Authentication", value: "Sign in now" },
  { label: "OAuth client", value: "Register automatically" },
];

const chatgptDetails: SetupField[] = [
  { label: "Name", value: "ChangeEngine" },
  { label: "Description", value: description },
  { label: "Server URL", connectionUrl: true },
  { label: "Authentication", value: "OAuth" },
];

export const ASSISTANTS: AssistantInfo[] = [
  {
    id: "claude",
    name: "Claude",
    plan: "Pro, Max, Team or Enterprise",
    logo: "https://images.changeengine.com/claude.webp",
    removePath:
      "Customize › Connectors (an Owner removes it in Organization settings › Connectors on Team or Enterprise)",
    paths: [
      {
        id: "pro_max",
        label: "Pro or Max",
        steps: [
          { text: "In Claude, open Customize › Connectors." },
          { text: "Click + Add, then choose Add custom connector." },
          { text: "Enter these details, then click Continue.", fields: claudeDetails },
          {
            text: "Make sure these sign-in settings are selected, then click Add.",
            fields: claudeSignIn,
          },
          { text: "Click Connect, sign in with your ChangeEngine account and click Allow." },
          { text: "Start a chat, open + › Connectors and switch ChangeEngine on." },
        ],
      },
      {
        id: "team_enterprise",
        label: "Team or Enterprise",
        steps: [
          {
            text: "An Owner opens Organization settings › Connectors and clicks Add.",
            admin: true,
          },
          { text: "Hover over Custom, then choose Web.", admin: true },
          {
            text: "Enter these details, then click Continue.",
            fields: claudeDetails,
            admin: true,
          },
          {
            text: "Make sure these sign-in settings are selected, then click Add.",
            fields: claudeSignIn,
            admin: true,
          },
          {
            text: "Everyone else opens Customize › Connectors, finds ChangeEngine and clicks Connect.",
          },
          {
            text: "Sign in with your ChangeEngine account, then switch it on in a chat from + › Connectors.",
          },
        ],
      },
    ],
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    plan: "Business, Enterprise or Edu",
    logo: "https://images.changeengine.com/chatgpt.webp",
    removePath:
      "Settings › Apps (a workspace admin removes a published app in Workspace settings › Apps)",
    paths: [
      {
        id: "workspace",
        label: "For the whole workspace",
        steps: [
          {
            text: "Open Workspace settings › Permissions & roles › Connected data and turn on Developer mode.",
            admin: true,
          },
          { text: "Go to Workspace settings › Apps and click Create.", admin: true },
          { text: "Enter these details.", fields: chatgptDetails, admin: true },
          {
            text: "Click Scan tools, sign in with your ChangeEngine account, then click Create.",
            admin: true,
          },
          {
            text: "Open Workspace settings › Apps › Drafts and click Publish so your team can use it.",
            admin: true,
          },
          { text: "In a chat, choose ChangeEngine from the + menu or type @ChangeEngine." },
        ],
      },
      {
        id: "just_me",
        label: "Just for me",
        steps: [
          {
            text: "Ask your admin to give you Developer mode in Permissions & roles › Connected data.",
            admin: true,
          },
          { text: "Open Settings › Apps › Advanced settings and turn on Developer mode." },
          { text: "Go to Settings › Apps and click Create." },
          { text: "Enter these details.", fields: chatgptDetails },
          {
            text: "Click Scan tools, sign in with your ChangeEngine account, then click Create.",
          },
          { text: "In a chat, choose ChangeEngine from the + menu or type @ChangeEngine." },
        ],
      },
    ],
  },
  {
    id: "copilot",
    name: "Microsoft Copilot",
    plan: "Microsoft 365 Copilot with Copilot Studio",
    logo: "https://images.changeengine.com/copilot.webp",
    removePath: "the agent’s Tools in Copilot Studio",
    paths: [
      {
        id: "copilot_studio",
        label: "Copilot Studio",
        steps: [
          {
            text: "Sign in to Copilot Studio and create or open an agent, for example “Comms assistant”.",
            admin: true,
          },
          {
            text: "Open Tools, then click Add a tool › New tool › Model Context Protocol.",
            admin: true,
          },
          {
            text: "Enter these details.",
            fields: [
              { label: "Server name", value: "ChangeEngine" },
              { label: "Server description", value: description },
              { label: "Server URL", connectionUrl: true },
            ],
            admin: true,
          },
          {
            text: "Choose this authentication, then click Create.",
            fields: [
              { label: "Authentication", value: "OAuth 2.0" },
              { label: "Type", value: "Dynamic discovery" },
            ],
            admin: true,
          },
          {
            text: "Select Create a new connection, sign in with your ChangeEngine account, then click Add to agent.",
            admin: true,
          },
          {
            text: "Publish the agent to the Microsoft 365 Copilot and Teams channels.",
            admin: true,
          },
          { text: "Your team opens Copilot, picks the agent and starts asking." },
        ],
      },
    ],
  },
];

export const assistantInfo = (assistant: Assistant): AssistantInfo =>
  ASSISTANTS.find(({ id }) => id === assistant)!;

const DATA_SHARING_AGREEMENT = [
  "By clicking Continue, you agree that ChangeEngine will share data from your organization’s ChangeEngine account with the AI assistants your team connects, starting with {tool}. You also confirm you’re authorized to accept this on your organization’s behalf.",
  "You only agree once. It covers your whole organization and every assistant, so teammates won’t be asked again.",
  "Your organization is the data controller for anything shared with an AI assistant, and that assistant’s own terms apply to it. ChangeEngine isn’t responsible for how {tool} or any other assistant handles that data.",
];

export const dataSharingAgreement = (assistant: Assistant): string[] =>
  DATA_SHARING_AGREEMENT.map((paragraph) =>
    paragraph.replaceAll("{tool}", assistantInfo(assistant).name),
  );

export const STARTER_PROMPTS = [
  {
    id: "create_comms",
    kind: "Create comms",
    text: "Draft an email and a matching Slack post announcing our new hybrid-working policy, scheduled for 9am Tuesday.",
  },
  {
    id: "build_program",
    kind: "Build a program",
    text: "Build a new-starter onboarding program around start date, with a manager prep email and a 30-day survey.",
  },
  {
    id: "engagement",
    kind: "Ask about engagement",
    text: "What were our five best-performing comms last quarter, and what do they have in common?",
  },
  {
    id: "chain",
    kind: "Chain your tools",
    text: "Read our time-off policy in Google Drive and turn it into an explainer email for new joiners.",
  },
];
